<template>
  <div v-if="showBadges" :class="classes.badgeWrapper">
    <TeamBadge
      :class="classes.badge"
      :borderColor="badgeBorderColor"
      :hideBoxShadow="true"
      :src="badge"
      :countryCode="countryCode"
    />
    <OptionButton
      :option="option"
      :class="[classes.listOption, 'test-option']"
      @click="handleClick"
    />
  </div>
  <div v-else ref="targetEl">
    <OptionButton
      :option="option"
      :class="[classes.listOption, 'test-option']"
      @click="handleClick"
    />
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { Option } from "@fresh8/types/src/betData";
import OptionButton from "../../OptionButton.vue";
import TeamBadge from "../../TeamBadge.vue";
import { PropType, defineComponent, computed, provide } from "vue";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { injectUseTeamBadges } from "../../../lib/teamBranding";
import { injectCardNumber, useAnalytics } from "../../../hooks/useAnalytics";
import { composeEventCompetitors } from "@fresh8/ad-data-composition/src/betData";

const useListOptionStyles = createUseStyles({
  badgeWrapper: {
    marginBottom: "10px",
    "& .test-option": {
      width: "93%",
      left: "7%",
      position: "relative",
      height: "40px"
    },
    "& .name": {
      paddingLeft: "15px"
    }
  },
  badge: {
    zIndex: 999,
    position: "absolute"
  },
  listOption: {
    height: "100%",
    paddingLeft: props => (props.badge ? "1px !important" : ""),
    borderTopLeftRadius: props => (props.badge ? "13px" : ""),
    borderBottomLeftRadius: props => (props.badge ? "13px" : ""),
    borderRadius: "10px",

    "&:before": {
      content: props => (props.badge ? "''" : ""),
      height: "26px",
      width: "26px",
      backgroundImage: props => `url(${props.badge})`,
      backgroundSize: "20px",
      backgroundColor: "white",
      borderRadius: "50%",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center"
    }
  }
});

export default defineComponent({
  components: {
    OptionButton,
    TeamBadge
  },
  props: {
    eventId: { type: String, required: true },
    option: { type: Object as PropType<Option>, required: true },
    marketId: { type: String, required: true },
    showBadge: {
      type: Boolean,
      default: false
    },
    stagger: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    provide("button-transition-stagger", props.stagger);
    const { adTemplate, getButtonStyle } =
      injectBrand() || ({} as BrandContext);
    const buttonId = computed(() => adTemplate.value?.market?.oddsButton);
    const badgesEnabled = injectUseTeamBadges(props.eventId);
    const competitors = composeEventCompetitors(props.eventId);
    const buttonStyle = computed(() => {
      return getButtonStyle(buttonId.value);
    });
    const badgeSrc = computed(() => {
      if (competitors.value) {
        switch (props.option.label) {
          case "home":
            return competitors.value[0].badge;
          case "away":
            return competitors.value[1].badge;
          default:
            return "";
        }
      }
      return "";
    });

    const countryCode = computed(() => {
      if (competitors.value) {
        switch (props.option.label) {
          case "home":
            return competitors.value[0].colors.country_code_a2;
          case "away":
            return competitors.value[1].colors.country_code_a2;
          default:
            return "";
        }
      }
      return "";
    });

    const reactiveStyles = computed(() => ({
      badge: props.option.badge
    }));

    const classes = useListOptionStyles(reactiveStyles);
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { ...props, cardNumber, optionId: props.option.id },
      "Odds Button"
    );

    const { targetEl } = composePopoverForm("Market Button", [
      {
        type: "button",
        get: () => adTemplate.value?.market.oddsButton ?? "",
        set: (value: string) => {
          if (adTemplate.value) {
            adTemplate.value.market.oddsButton = value;
          }
        }
      }
    ]);

    const handleButtonClick = () => {
      if (props.option.suspended || !props.option) return;
      handleClick();
    };

    return {
      classes,
      badge: badgeSrc.value,
      countryCode,
      showBadges: props.showBadge && badgesEnabled.value,
      badgeBorderColor: buttonStyle.value.backgroundColor,
      handleClick: handleButtonClick,
      targetEl
    };
  }
});
</script>
